import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePreguntasByTema } from '../hooks/useDataService';
import MathGraph from './MathGraph';
import { QuestionText, OptionText, isImageDataOrUrl } from './MathRenderer';
import '../styles/Quiz.css';
import '../styles/MathGraph.css';

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Obtener temaId de la URL
  const temaId = parseInt(location.pathname.split('/').pop());
  const areaId = location.state?.areaId;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutos default
  const [timerActive, setTimerActive] = useState(true);
  const [score, setScore] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const { preguntas, loading, error, source, refetch } = usePreguntasByTema(temaId, {
    count: 20,
    random: true
  });

  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  // Timer
  useEffect(() => {
    if (!timerActive || quizCompleted) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timerActive, quizCompleted]);

  const handleTimeUp = useCallback(() => {
    setTimerActive(false);
    finishQuiz();
  }, []);

  const finishQuiz = useCallback(() => {
    let correct = 0;
    preguntas.forEach((p, i) => {
      if (answers[i] === p.correcta || answers[i] === p.correctAnswer || answers[i] === p.correctIndex) {
        correct++;
      }
    });
    setScore(correct);
    setQuizCompleted(true);
    setShowResult(true);
    setTimerActive(false);
  }, [preguntas, answers]);

  const handleAnswer = useCallback((questionIndex, answer) => {
    setAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentIndex < preguntas.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, preguntas.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const handleRetry = useCallback(() => {
    setCurrentIndex(0);
    setAnswers({});
    setQuizCompleted(false);
    setShowResult(false);
    setScore(0);
    setTimeLeft(30 * 60);
    setTimerActive(true);
    setReviewMode(false);
    startTimeRef.current = Date.now();
    refetch(); // Obtener nuevas preguntas aleatorias
  }, [refetch]);

  const handleReview = useCallback(() => {
    setReviewMode(true);
    setShowResult(false);
  }, []);

  const handleExit = useCallback(() => {
    if (quizCompleted || Object.keys(answers).length === 0) {
      navigate('/dashboard/quiz', { state: { areaId } });
    } else {
      setShowExitConfirm(true);
    }
  }, [quizCompleted, answers, navigate, areaId]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer circular SVG
  const TimerCircle = ({ timeLeft, totalTime }) => {
    const pct = timeLeft / totalTime;
    const r = 18;
    const circ = 2 * Math.PI * r;
    const dash = circ * pct;
    const isWarning = timeLeft < 300;
    const isDanger  = timeLeft < 60;
    const color = isDanger ? '#f44336' : isWarning ? '#ff9800' : 'var(--gold-matte)';

    return (
      <div className="quiz-timer-wrap" title={`Tiempo restante: ${formatTime(timeLeft)}`}>
        <svg width="44" height="44" viewBox="0 0 44 44">
          <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(197,160,89,0.12)" strokeWidth="3"/>
          <circle
            cx="22" cy="22" r={r} fill="none"
            stroke={color}
            strokeWidth="3"
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            transform="rotate(-90 22 22)"
            style={{ transition: 'stroke-dasharray 0.9s linear, stroke 0.3s' }}
          />
        </svg>
        <span className="quiz-timer-text" style={{ color }}>{formatTime(timeLeft)}</span>
      </div>
    );
  };

  const getCorrectAnswer = (p) => {
    return p.correcta ?? p.correctAnswer ?? p.correctIndex ?? 0;
  };

  const getRespuestas = (p) => {
    if (p.respuestas && typeof p.respuestas === 'object') {
      return Object.values(p.respuestas).map(r => {
        if (typeof r === 'string') return r;
        return r.respuesta || r.texto || r.imagen || r.url || r;
      });
    }
    if (p.opciones && Array.isArray(p.opciones)) {
      return p.opciones.map(r => {
        if (typeof r === 'string') return r;
        return r.respuesta || r.texto || r.imagen || r.url || r;
      });
    }
    return [];
  };

  // Guardar resultado al completar
  useEffect(() => {
    if (quizCompleted && user && preguntas?.length > 0) {
      const resultData = {
        temaId,
        areaId,
        score,
        total: preguntas.length,
        percentage: Math.round((score / preguntas.length) * 100),
        timeSpent: Math.floor((Date.now() - startTimeRef.current) / 1000),
        answers: Object.entries(answers).map(([idx, ans]) => ({
          questionId: preguntas[idx]?.id,
          userAnswer: ans,
          correctAnswer: getCorrectAnswer(preguntas[idx]),
          isCorrect: ans === getCorrectAnswer(preguntas[idx])
        }))
      };

      // Guardar en IndexedDB para sincronización offline
      import('../services/dataService').then(({ default: dataService }) => {
        dataService.saveQuizResult(user.uid, resultData).catch(console.error);
      });
    }
  }, [quizCompleted, user, temaId, areaId, score, preguntas, answers]);

  if (loading) {
    return (
      <div className="quiz-container">
        <div className="quiz-loading">
          <div className="quiz-spinner" />
          <p>Cargando preguntas{source !== 'local' ? ` (${source})` : ''}…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container">
        <div className="quiz-state-card quiz-error">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ff5252" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h3>Error al cargar el quiz</h3>
          <p>{error}</p>
          <div className="quiz-state-actions">
            <button onClick={refetch} className="btn-primary">Reintentar</button>
            <button onClick={() => navigate('/dashboard/quiz', { state: { areaId } })} className="btn-secondary">Volver</button>
          </div>
        </div>
      </div>
    );
  }

  if (!preguntas || preguntas.length === 0) {
    return (
      <div className="quiz-container">
        <div className="quiz-state-card quiz-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--gold-matte)" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h3>No hay preguntas disponibles</h3>
          <p>Este tema no tiene preguntas cargadas aún.</p>
          <button onClick={() => navigate('/dashboard/quiz', { state: { areaId } })} className="btn-primary">
            Volver a Áreas
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = preguntas[currentIndex];
  const respuestas = getRespuestas(currentQuestion);
  const correctAnswer = getCorrectAnswer(currentQuestion);
  const userAnswer = answers[currentIndex];
  const isAnswered = userAnswer !== undefined;
  const isCorrect = isAnswered && userAnswer === correctAnswer;
  const progressPct = Math.round(((currentIndex + 1) / preguntas.length) * 100);
  const answeredCount = Object.keys(answers).length;

  // Detectar si la pregunta tiene gráfica
  const hasGraph = !!currentQuestion.grafica;

  // Confirmar salida
  if (showExitConfirm) {
    return (
      <div className="quiz-container">
        <div className="quiz-state-card">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--gold-matte)" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <h3>¿Salir sin terminar?</h3>
          <p>Tu progreso se guardará automáticamente.</p>
          <div className="quiz-state-actions">
            <button onClick={() => setShowExitConfirm(false)} className="btn-secondary">Continuar quiz</button>
            <button onClick={() => {
              setShowExitConfirm(false);
              navigate('/dashboard/quiz', { state: { areaId } });
            }} className="btn-danger">Salir</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">

      {/* ── Header ── */}
      <header className="quiz-header-v2">
        <button onClick={handleExit} className="quiz-back-btn" aria-label="Volver">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Salir</span>
        </button>

        <div className="quiz-progress-wrap">
          <div className="quiz-progress-bar-v2" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
            <div className="quiz-progress-fill-v2" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="quiz-progress-label">
            <strong>{currentIndex + 1}</strong> / {preguntas.length}
            {answeredCount > 0 && !quizCompleted && (
              <span className="quiz-answered-badge">{answeredCount} respondidas</span>
            )}
          </span>
        </div>

        <TimerCircle timeLeft={timeLeft} totalTime={30 * 60} />
      </header>

      {/* ── Modo Quiz activo ── */}
      {!quizCompleted && (
        <main className="quiz-main-v2">
          <div className={`quiz-card-v2 ${hasGraph ? 'has-graph' : ''}`}>

            {/* Número de pregunta + badge modo revisión */}
            <div className="quiz-q-meta">
              <span className="quiz-q-number">Pregunta {currentIndex + 1}</span>
              {reviewMode && (
                <span className={`quiz-answer-badge ${isCorrect ? 'correct' : isAnswered ? 'incorrect' : 'unanswered'}`}>
                  {isCorrect ? '✓ Correcta' : isAnswered ? '✗ Incorrecta' : 'Sin responder'}
                </span>
              )}
            </div>

            {/* Layout: con/sin gráfica */}
            <div className={hasGraph ? 'question-with-graph' : ''}>
              <div className={hasGraph ? 'question-text-col' : ''}>
                <div className="quiz-question-text">
                  <QuestionText text={currentQuestion.pregunta || currentQuestion.question || currentQuestion.text} />
                </div>
              </div>

              {hasGraph && (
                <div className="graph-col">
                  <MathGraph config={currentQuestion.grafica} />
                </div>
              )}
            </div>

            {/* Opciones de respuesta */}
            <div className="quiz-options-v2">
              {respuestas.map((respuesta, idx) => {
                const isSelected   = userAnswer === idx;
                const showCorrect  = reviewMode && idx === correctAnswer;
                const showWrong    = reviewMode && isSelected && !isCorrect;
                const hasImg       = isImageDataOrUrl(respuesta);

                return (
                  <button
                    key={idx}
                    className={`quiz-option-v2 ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showWrong ? 'incorrect' : ''} ${hasImg ? 'has-image' : ''}`}
                    onClick={() => !quizCompleted && !reviewMode && handleAnswer(currentIndex, idx)}
                    disabled={quizCompleted || reviewMode}
                    aria-label={`Opción ${String.fromCharCode(65 + idx)}`}
                    aria-pressed={isSelected}
                  >
                    <span className="quiz-option-letter">{String.fromCharCode(65 + idx)}</span>
                    <span className="quiz-option-text">
                      <OptionText text={respuesta} />
                    </span>
                    {showCorrect && <span className="quiz-option-icon correct" aria-label="Respuesta correcta">✓</span>}
                    {showWrong   && <span className="quiz-option-icon incorrect" aria-label="Tu respuesta">✗</span>}
                  </button>
                );
              })}
            </div>

            {/* Explicación en modo revisión */}
            {currentQuestion.explicacion && reviewMode && (
              <div className="quiz-explanation-v2">
                <div className="quiz-explanation-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  Explicación
                </div>
                <div className="quiz-explanation-body">
                  <QuestionText text={currentQuestion.explicacion} />
                </div>
              </div>
            )}
          </div>

          {/* ── Navegación ── */}
          <nav className="quiz-nav-v2" aria-label="Navegación de preguntas">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0 || quizCompleted}
              className="quiz-nav-btn secondary"
              aria-label="Pregunta anterior"
            >
              ← Anterior
            </button>

            {/* Dots de preguntas */}
            <div className="quiz-dots" role="list" aria-label="Estado de preguntas">
              {preguntas.map((_, idx) => {
                const isActive  = idx === currentIndex;
                const isAns     = answers[idx] !== undefined;
                const isCor     = reviewMode && answers[idx] === getCorrectAnswer(preguntas[idx]);
                const isErr     = reviewMode && isAns && answers[idx] !== getCorrectAnswer(preguntas[idx]);
                return (
                  <button
                    key={idx}
                    role="listitem"
                    className={`quiz-dot ${isActive ? 'active' : ''} ${isAns ? 'answered' : ''} ${isCor ? 'correct' : ''} ${isErr ? 'incorrect' : ''}`}
                    onClick={() => !quizCompleted && !reviewMode && setCurrentIndex(idx)}
                    disabled={quizCompleted || reviewMode}
                    aria-label={`Pregunta ${idx + 1}${isAns ? ' (respondida)' : ''}`}
                    title={`Pregunta ${idx + 1}`}
                  />
                );
              })}
            </div>

            <button
              onClick={currentIndex === preguntas.length - 1 ? finishQuiz : handleNext}
              disabled={quizCompleted}
              className={`quiz-nav-btn primary ${currentIndex === preguntas.length - 1 ? 'finish' : ''}`}
              aria-label={currentIndex === preguntas.length - 1 ? 'Finalizar quiz' : 'Siguiente pregunta'}
            >
              {currentIndex === preguntas.length - 1 ? 'Finalizar ✓' : 'Siguiente →'}
            </button>
          </nav>
        </main>
      )}

      {/* ── Pantalla de Resultados ── */}
      {quizCompleted && (
        <main className="quiz-results-v2">
          {/* Círculo de puntuación */}
          <div className="quiz-score-ring-wrap">
            {(() => {
              const pct = Math.round((score / preguntas.length) * 100);
              const r = 52; const circ = 2 * Math.PI * r;
              const color = pct >= 80 ? '#4caf50' : pct >= 60 ? 'var(--gold-matte)' : pct >= 40 ? '#ff9800' : '#ff5252';
              return (
                <>
                  <svg width="130" height="130" viewBox="0 0 130 130" className="quiz-score-ring">
                    <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(197,160,89,0.1)" strokeWidth="8"/>
                    <circle cx="65" cy="65" r={r} fill="none" stroke={color}
                      strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={`${circ * pct / 100} ${circ}`}
                      transform="rotate(-90 65 65)"
                      style={{ transition: 'stroke-dasharray 1.2s ease-out' }}
                    />
                  </svg>
                  <div className="quiz-score-inner">
                    <span className="quiz-score-num" style={{ color }}>{score}</span>
                    <span className="quiz-score-den">/{preguntas.length}</span>
                    <span className="quiz-score-pct" style={{ color }}>{pct}%</span>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="quiz-result-msg">
            {score === preguntas.length ? '¡Perfecto! 🎉' :
             score >= preguntas.length * 0.8 ? '¡Muy bien! 👏' :
             score >= preguntas.length * 0.6 ? 'Buen trabajo 👍' :
             '¡Sigue practicando! 💪'}
          </div>

          {/* Stats */}
          <div className="quiz-stats-grid">
            {[
              { label: 'Tiempo', value: formatTime(Math.floor((Date.now() - startTimeRef.current) / 1000)) },
              { label: 'Correctas', value: score },
              { label: 'Incorrectas', value: preguntas.length - score },
              { label: 'Fuente', value: source === 'local' ? '💾 Offline' : '☁️ Online' },
            ].map(({ label, value }) => (
              <div className="quiz-stat-card" key={label}>
                <span className="quiz-stat-val">{value}</span>
                <span className="quiz-stat-lbl">{label}</span>
              </div>
            ))}
          </div>

          {/* Acciones */}
          <div className="quiz-result-actions">
            <button onClick={handleReview} className="quiz-action-btn secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
              Revisar respuestas
            </button>
            <button onClick={handleRetry} className="quiz-action-btn primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              Nuevo intento
            </button>
            <button onClick={() => navigate('/dashboard/quiz', { state: { areaId } })} className="quiz-action-btn outline">
              Volver a Temas
            </button>
          </div>

          {/* Lista de revisión */}
          {reviewMode && (
            <div className="quiz-review-list">
              <h3 className="quiz-review-title">Revisión de respuestas</h3>
              {preguntas.map((p, idx) => {
                const uAns = answers[idx];
                const cAns = getCorrectAnswer(p);
                const ok   = uAns === cAns;
                const ans  = uAns !== undefined;
                return (
                  <div key={idx} className={`quiz-review-item ${ok ? 'correct' : ans ? 'incorrect' : 'unanswered'}`}>
                    <div className="quiz-review-q">
                      <span className="quiz-review-q-num">P{idx + 1}.</span>
                      <QuestionText text={p.pregunta || p.question || p.text} />
                    </div>
                    <div className="quiz-review-opts">
                      {getRespuestas(p).map((r, ri) => (
                        <div key={ri} className={`quiz-review-opt ${ri === cAns ? 'correct-ans' : ''} ${ri === uAns && !ok ? 'user-ans' : ''}`}>
                          <span className="quiz-review-letter">{String.fromCharCode(65 + ri)}</span>
                          <span><OptionText text={r} /></span>
                          {ri === cAns && <span className="review-badge correct">✓ Correcta</span>}
                          {ri === uAns && !ok && <span className="review-badge incorrect">Tu resp.</span>}
                        </div>
                      ))}
                    </div>
                    {p.explicacion && (
                      <div className="quiz-review-exp">
                        💡 <QuestionText text={p.explicacion} />
                      </div>
                    )}
                    {p.grafica && (
                      <div className="quiz-review-graph">
                        <MathGraph config={p.grafica} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default Quiz;